import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Country, Station } from '@vueling-ng/vy-cdk-searcher';
import { genericStation, StationMacs } from 'app/services/internal/static/models/stations.interface';
import { combineLatest, distinctUntilChanged, forkJoin, map, Observable, switchMap } from 'rxjs';
import { StateManagerService } from '../../../shared/state/state-manager.service';

@Injectable({ providedIn: 'root' })
export class StaticsService {
  private readonly _document = inject(DOCUMENT);
  private readonly _stateManagerService: StateManagerService = inject(StateManagerService);
  private readonly _httpClient = inject(HttpClient);

  private readonly _lang$: Observable<string> = this._stateManagerService.state$.pipe(
    distinctUntilChanged(),
    map((state) => state.language!)
  );

  private readonly _updateAsset$: Observable<{ stations: Station[]; countries: Country[] }> = this._lang$.pipe(
    distinctUntilChanged(),
    switchMap((language) => {
      return this.getStatics(language);
    })
  );

  public initialize(): Observable<{ stations: Station[]; countries: Country[] }> {
    return this._updateAsset$;
  }

  public getStatics(language: string): Observable<{ stations: Station[]; countries: Country[] }> {
    return combineLatest({
      stations: this.getMergedStationsWithMacs(language),
      countries: this.getCountries(language)
    });
  }

  private getMergedStationsWithMacs(language: string): Observable<Station[]> {
    return forkJoin([this.getStations(language), this.getMacsStations(language)]).pipe(
      map(([stations, macsStation]) => {
        const macsStations: Station[] = [...stations];
        macsStation.forEach((mac) => {
          const matchedResult = stations.find((station) => station?.macCode === mac?.code);
          if (matchedResult && !stations.some((station) => station.stationCode === mac?.code)) {
            const macStation: Station = {
              ...genericStation,
              fullName: mac.name,
              locationDetails: {
                ...genericStation.locationDetails,
                countryCode: mac.countryCode,
                timeZoneCode: mac.countryCode
              },
              shortName: mac.name,
              stationCode: mac.code,
              macCode: mac.code
            };
            macsStations.push(macStation);
          }
        });
        return macsStations;
      })
    );
  }

  private getStations(language: string): Observable<Station[]> {
    const origin = this._document.location.origin;
    return this._httpClient.get<Station[]>(`${origin}/assets/stations/${language}.json`);
  }

  private getMacsStations(language: string): Observable<StationMacs[]> {
    const origin = this._document.location.origin;
    return this._httpClient.get<StationMacs[]>(`${origin}/assets/stations/macs/${language}.json`);
  }

  private getCountries(language: string): Observable<Country[]> {
    const origin = this._document.location.origin;
    return this._httpClient.get<Country[]>(`${origin}/assets/countries/${language}.json`);
  }
}
